import "antd/dist/antd.css";
import "./Form.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Input, Segmented, Select } from "antd";
import { HiddenFields } from "./HiddenFields";
import { TemplateSearchField } from "./TemplateSearchField";
import { OptionRow } from "./OptionRow";
import { BranchFields, getAttachableCompanies } from "./BranchFields";
import { GoBackButton } from "../Components/GoBackButton";

const SETUP_TYPES = [
  { value: "standard", label: "Standard, full dummy data" },
  { value: "customTemplate", label: "Custom template, copy a company" },
  { value: "empty", label: "Empty, minimal setup" },
];

const PROVIDERS = [
  { value: "sfs", label: "SFS" },
  { value: "bankable", label: "Bankable" },
];

const CURRENCIES = [
  { value: "EUR", label: "Euro (€)" },
  { value: "GBP", label: "GBP (£)" },
];

const LANGUAGES = [
  { value: "fr", label: "Français (FR)" },
  { value: "en", label: "English (EN)" },
  { value: "de", label: "Deutsch (DE)" },
  { value: "es", label: "Español (ES)" },
  { value: "it", label: "Italiano (IT)" },
];

const BILLING_PLANS = [
  { value: "freemium", label: "Freemium" },
  { value: "starter", label: "Starter" },
  { value: "essentials", label: "Essentials" },
  { value: "scale", label: "Scale" },
  { value: "scale-2023", label: "Scale 2023" },
  { value: "enterprise", label: "Enterprise" },
  { value: "premium", label: "Premium" },
  { value: "cards", label: "Smart Cards" },
];

const labelOf = (options, value) =>
  (options.find((o) => o.value === value) || {}).label;

export default function FormPlatform({
  onSubmit,
  userEmail,
  onHomepageClick,
  processes = [],
}) {
  const [form] = Form.useForm();

  const companyName = Form.useWatch("user.organisation.name", form);
  const firstName = Form.useWatch("user.first_name", form);
  const lastName = Form.useWatch("user.last_name", form);
  const billingPlan = Form.useWatch("config.billing_subscription_plan", form);
  const templateCompanyId = Form.useWatch("config.templateCompanyId", form);
  const configurationType = Form.useWatch("config.configurationType", form);
  const provider = Form.useWatch("config.banking_provider", form);
  const currency = Form.useWatch("user.organisation.currency", form);
  const branchMode = Form.useWatch("config.branch.mode", form);
  const branchAttachId = Form.useWatch("config.branch.attachCompanyId", form);
  const branchName = Form.useWatch("config.branch.name", form);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [unlockAllFeatures, setUnlockAllFeatures] = useState(true);
  const [branchOn, setBranchOn] = useState(false);
  const branchNameEdited = useRef(false);

  const isCustomTemplate = configurationType === "customTemplate";
  const isWalletAvailable =
    provider === "sfs" && (isCustomTemplate || currency === "EUR");
  const isEmpty = configurationType === "empty";

  const attachable = useMemo(
    () => getAttachableCompanies(processes, userEmail),
    [processes, userEmail]
  );
  const attachTarget = attachable.find((c) => c.company_id === branchAttachId);

  useEffect(() => {
    if (configurationType !== "customTemplate") {
      form.setFieldsValue({ "config.templateCompanyId": "" });
    }
  }, [configurationType, form]);

  useEffect(() => {
    if (!branchNameEdited.current) {
      form.setFieldsValue({
        "config.branch.name": companyName?.trim()
          ? `${companyName.trim()} Branch`
          : "",
      });
    }
  }, [companyName, form]);

  const onUnlockChange = (checked) => {
    setUnlockAllFeatures(checked);
    form.setFieldsValue({
      "config.billing_subscription_plan": checked ? "demo" : undefined,
    });
  };

  const isBranchValid =
    !branchOn ||
    (branchMode === "attach"
      ? Boolean(attachTarget && !attachTarget.lockedReason)
      : Boolean(branchName?.trim()));

  const isFormValid =
    companyName?.trim() &&
    configurationType &&
    firstName?.trim() &&
    lastName?.trim() &&
    billingPlan &&
    (!isCustomTemplate || Boolean(templateCompanyId)) &&
    isBranchValid;

  const currencies = {
    fr: { currency: "EUR", country: "FR" },
    uk: { currency: "GBP", country: "GB" },
  };

  const onSubmitForm = async (data) => {
    data["user.organisation"] = currencies[data["user.organisation"]];

    if (isCustomTemplate && templateCompanyId?.trim()) {
      data["config.templateCompanyId"] = templateCompanyId.trim();
    }

    Object.keys(data)
      .filter((key) => key.startsWith("config.branch."))
      .forEach((key) => delete data[key]);

    if (branchOn) {
      data["config.branch.mode"] = branchMode;
      if (branchMode === "attach") {
        data["config.branch.attachCompanyId"] = branchAttachId;
      } else {
        data["config.branch.name"] = branchName.trim();
        data["config.branch.content"] = isEmpty
          ? "empty"
          : form.getFieldValue("config.branch.content");
      }
    }

    if (
      branchOn &&
      isWalletAvailable &&
      form.getFieldValue("config.branch.currencyWallet")
    ) {
      data["config.branch.currencyWallet"] = true;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  let submitLabel = "Create a company";
  if (branchOn && branchMode === "create") {
    submitLabel = "Create company and branch";
  } else if (branchOn && branchMode === "attach") {
    if (attachTarget) {
      submitLabel = `Create, then attach ${attachTarget.company_name}`;
    } else {
      submitLabel = "Pick a company to attach";
    }
  }

  const inheritedTags = [
    isCustomTemplate ? "Currency as template" : currency,
    labelOf(PROVIDERS, provider),
    unlockAllFeatures
      ? "Demo plan"
      : labelOf(BILLING_PLANS, billingPlan) || "No plan yet",
  ].filter(Boolean);

  return (
    <div className="md-page">
      <div className="md-top">
        <GoBackButton onClick={onHomepageClick} />
        <h1 className="md-title">Create a company</h1>
      </div>

      <div className="md-card">
        <Form
          className="md-form"
          layout="vertical"
          form={form}
          name="basic"
          onFinish={onSubmitForm}
          initialValues={{
            "config.amount_to_load": "100000",
            "user.organisation.currency": "EUR",
            "config.configurationType": "standard",
            "config.banking_provider": "sfs",
            "config.usersLanguage": "fr",
            "config.billing_subscription_plan": "demo",
            "config.branch.mode": "create",
            "config.branch.content": "empty",
            "config.branch.currencyWallet": false,
          }}
        >
          <HiddenFields name={"config.amount_to_load"} value={100000} />
          <HiddenFields name={"config.created_by"} value={userEmail} />

          <div className="md-grid">
            <Form.Item
              className="md-s3"
              label="Company name"
              name="user.organisation.name"
              required
            >
              <Input autoComplete="off" />
            </Form.Item>

            <Form.Item
              className="md-s3"
              label="Setup type"
              name="config.configurationType"
              required
            >
              <Select options={SETUP_TYPES} popupClassName="md-dropdown" />
            </Form.Item>

            <Form.Item
              className="md-s2"
              label="Banking provider"
              name="config.banking_provider"
            >
              <Segmented block options={PROVIDERS} />
            </Form.Item>

            {isCustomTemplate && (
              <Form.Item
                className="md-s4 md-rise"
                label="Template company"
                name="config.templateCompanyId"
                required
              >
                <TemplateSearchField form={form} />
              </Form.Item>
            )}

            <div className="md-s2 md-rise" hidden={isCustomTemplate}>
              <Form.Item
                label="Currency"
                name="user.organisation.currency"
                required
              >
                <Segmented block options={CURRENCIES} />
              </Form.Item>
            </div>

            <div className="md-s2 md-rise" hidden={isCustomTemplate}>
              <Form.Item label="Language" name="config.usersLanguage" required>
                <Select options={LANGUAGES} popupClassName="md-dropdown" />
              </Form.Item>
            </div>

            <Form.Item
              className="md-s3"
              label="AO's first name"
              name="user.first_name"
              required
            >
              <Input autoComplete="off" />
            </Form.Item>

            <Form.Item
              className="md-s3"
              label="AO's last name"
              name="user.last_name"
              required
            >
              <Input autoComplete="off" />
            </Form.Item>
          </div>

          <OptionRow
            id="md-unlock-title"
            title="Unlock all features"
            subtitle="Full access to all add-ons, no billing limits"
            checked={unlockAllFeatures}
            open={!unlockAllFeatures}
            onChange={onUnlockChange}
          >
            <Form.Item
              label="Billing subscription plan"
              name="config.billing_subscription_plan"
              required
            >
              <Select
                placeholder="Select a plan"
                options={BILLING_PLANS}
                popupClassName="md-dropdown"
              />
            </Form.Item>
          </OptionRow>

          <OptionRow
            id="md-branch-title"
            title="Add a branch"
            subtitle="To show the multi-entity hub"
            checked={branchOn}
            open={branchOn}
            onChange={setBranchOn}
          >
            <BranchFields
              form={form}
              companies={attachable}
              isMainEmpty={isEmpty}
              inheritedTags={inheritedTags}
              isWalletAvailable={isWalletAvailable}
              onNameEdited={() => {
                branchNameEdited.current = true;
              }}
            />
          </OptionRow>

          <div className="md-cta">
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={isSubmitting}
              disabled={!isFormValid}
            >
              {isSubmitting
                ? isEmpty
                  ? "Creating empty account..."
                  : "Creating company..."
                : submitLabel}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
